import { Fragment, ReactNode, useEffect } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { useToasts } from '@/hooks/useToasts'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Card,
  type ColorScheme,
  CopyIcon,
  HStack,
  IconButton,
  Skeleton,
  Text,
  VStack,
} from '@hivewatch/dark'

import { useDeviceMetaDataQuery } from '@/graphql/generated/hooks'
import { TAB_PRESS, trackEvent } from '@/utils/analytics'
import { tVar } from '@/utils/i18n'

interface IProps {
  id: string
}

/**
 * Small subtle dark `IconButton` + `CopyIcon` that copies a field value to the
 * clipboard, confirming through the rebrand toast (ENG-32123, Phase 2c).
 */
const DarkInfoCopyButton = ({
  colorScheme,
  textToCopy,
}: {
  colorScheme: ColorScheme
  textToCopy: string
}) => {
  const { t } = useTranslation()
  const { showError, showSuccess } = useToasts({ isRebrand: true, colorScheme })

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy)
      showSuccess(t('common:copied'))
    } catch {
      showError(t('devices:drawer_copy_error'))
    }
  }

  return (
    <IconButton
      aria-label={t('common:copy_to_clipboard')}
      borderRadius='6px'
      colorScheme={colorScheme}
      dimension={28}
      icon={<CopyIcon height={14} width={14} />}
      onClick={handleCopy}
    />
  )
}

/**
 * One plain `Label: value` row — bold label, monospace value, and an optional
 * trailing copy button — matching the Device Info mock.
 */
const DarkInfoField = ({
  canCopy = true,
  colorScheme,
  hasColon = true,
  label,
  value,
}: {
  canCopy?: boolean
  colorScheme: ColorScheme
  hasColon?: boolean
  label: string
  value?: string | number | null
}) => {
  const prefix = colorScheme === 'light' ? 'light.' : ''
  const displayValue =
    value === null || value === undefined ? '' : String(value)

  return (
    <HStack alignItems='center' gap='8px'>
      <Text color={`${prefix}text.primary`} textStyle='body-bold'>
        {hasColon ? `${label}:` : label}
      </Text>
      <Text color={`${prefix}text.secondary`} textStyle='accent'>
        {displayValue || '—'}
      </Text>
      {canCopy && displayValue && (
        <DarkInfoCopyButton
          colorScheme={colorScheme}
          textToCopy={displayValue}
        />
      )}
    </HStack>
  )
}

/**
 * Plain-header section — leading icons dropped per ENG-31744 — wrapping its rows
 * in a bordered dark `Card`.
 */
const DarkInfoSection = ({
  children,
  colorScheme,
  title,
}: {
  children: ReactNode
  colorScheme: ColorScheme
  title: string
}) => {
  const prefix = colorScheme === 'light' ? 'light.' : ''

  return (
    <VStack align='stretch' gap='12px'>
      <Text color={`${prefix}text.primary`} textStyle='body-large-bold'>
        {title}
      </Text>
      <Card boxShadow='none' colorScheme={colorScheme} p='20px'>
        <VStack align='stretch' gap='16px'>
          {children}
        </VStack>
      </Card>
    </VStack>
  )
}

/**
 * Rebrand (ENG-32123, Phase 2c) Device Info tab: an Implementer-only callout on
 * the dark `Alert`, plain section headers, and bordered `Card` sections of
 * inline `Label: value` rows — replacing the legacy Chakra-v1 sections.
 */
export const DarkDeviceInfoTab = ({ id }: IProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const prefix = colorScheme === 'light' ? 'light.' : ''

  const { data, loading: isLoading } = useDeviceMetaDataQuery({
    variables: { id },
    skip: !id,
  })

  useEffect(() => {
    trackEvent(`${TAB_PRESS} Device Drawer - Device Info`, { device_id: id })
  }, [id])

  const device = data?.device
  const gateway = device?.edgeDevice
  const tailgateConnections = device?.tailgateConns?.edges ?? []

  if (isLoading) {
    return (
      <VStack align='stretch' gap='16px' px='24px' py='16px'>
        <Skeleton borderRadius='8px' h='48px' />
        <Skeleton borderRadius='8px' h='160px' />
        <Skeleton borderRadius='8px' h='160px' />
      </VStack>
    )
  }

  return (
    <VStack align='stretch' gap='24px' px='24px' py='16px'>
      <Alert
        colorScheme={colorScheme}
        description={t('devices:drawer_implementers_only')}
        variant='info'
      />

      <DarkInfoSection
        colorScheme={colorScheme}
        title={t('devices:drawer_section_device')}
      >
        <DarkInfoField
          colorScheme={colorScheme}
          label={t('devices:drawer_field_device_id')}
          value={id}
        />
        <DarkInfoField
          colorScheme={colorScheme}
          label={t('devices:drawer_field_created_at')}
          value={device?.createdAt}
        />
        <DarkInfoField
          colorScheme={colorScheme}
          label={t('devices:drawer_field_updated_at')}
          value={device?.updatedAt}
        />
        <DarkInfoField
          canCopy={false}
          colorScheme={colorScheme}
          hasColon={false}
          label={t('devices:drawer_field_is_placed')}
          value={`${device?.isPlaced ?? false}`}
        />
      </DarkInfoSection>

      {device?.camera && (
        <DarkInfoSection
          colorScheme={colorScheme}
          title={t('devices:drawer_section_camera')}
        >
          <DarkInfoField
            colorScheme={colorScheme}
            label={t('devices:drawer_field_stream_width')}
            value={device.camera.streamingResolutionWidth}
          />
          <DarkInfoField
            colorScheme={colorScheme}
            label={t('devices:drawer_field_stream_height')}
            value={device.camera.streamingResolutionHeight}
          />
          <DarkInfoField
            colorScheme={colorScheme}
            label={t('devices:drawer_field_created_at')}
            value={device.camera.createdAt}
          />
          <DarkInfoField
            colorScheme={colorScheme}
            label={t('devices:drawer_field_updated_at')}
            value={device.camera.updatedAt}
          />
        </DarkInfoSection>
      )}

      {device?.door && (
        <DarkInfoSection
          colorScheme={colorScheme}
          title={t('devices:drawer_section_door')}
        >
          <DarkInfoField
            colorScheme={colorScheme}
            label={t('devices:drawer_field_external_id')}
            value={device.door.externalId}
          />
          <DarkInfoField
            colorScheme={colorScheme}
            label={t('devices:drawer_field_created_at')}
            value={device.door.createdAt}
          />
          <DarkInfoField
            colorScheme={colorScheme}
            label={t('devices:drawer_field_updated_at')}
            value={device.door.updatedAt}
          />
        </DarkInfoSection>
      )}

      {device?.camera && (
        <DarkInfoSection
          colorScheme={colorScheme}
          title={t('devices:drawer_section_gateway')}
        >
          {gateway?.id || gateway?.name ? (
            <>
              <DarkInfoField
                colorScheme={colorScheme}
                label={t('devices:drawer_field_gateway_id')}
                value={gateway?.id}
              />
              <DarkInfoField
                colorScheme={colorScheme}
                label={t('devices:drawer_field_gateway_name')}
                value={gateway?.name}
              />
            </>
          ) : (
            <Text color={`${prefix}text.secondary`}>
              {t('devices:drawer_field_not_connected')}
            </Text>
          )}
        </DarkInfoSection>
      )}

      <VStack align='stretch' gap='12px'>
        <Text color={`${prefix}text.primary`} textStyle='body-large-bold'>
          {t('devices:drawer_section_associations')}
        </Text>
        {tailgateConnections.length === 0 ? (
          <Card boxShadow='none' colorScheme={colorScheme} p='20px'>
            <Text color={`${prefix}text.secondary`}>
              {t('devices:drawer_no_associated_devices')}
            </Text>
          </Card>
        ) : (
          tailgateConnections.map((connection, index) => (
            <Card
              boxShadow='none'
              colorScheme={colorScheme}
              key={connection?.node?.id || index}
              p='20px'
            >
              <VStack align='stretch' gap='16px'>
                <Text color={`${prefix}text.secondary`} textStyle='body-bold'>
                  {tVar(t, 'devices:drawer_field_device_association', {
                    number: index + 1,
                  })}
                </Text>
                <Text color={`${prefix}text.secondary`} textStyle='label'>
                  {t('devices:drawer_section_connected_device')}
                </Text>
                <DarkInfoField
                  colorScheme={colorScheme}
                  label={t('devices:drawer_field_device_name')}
                  value={connection?.node?.door?.name}
                />
                <DarkInfoField
                  colorScheme={colorScheme}
                  label={t('devices:drawer_field_device_id')}
                  value={connection?.node?.door?.id}
                />

                {connection?.node?.cameras?.edges?.map(
                  (camera, cameraIndex) => (
                    <Fragment key={camera?.node?.id || cameraIndex}>
                      <Text color={`${prefix}text.secondary`} textStyle='label'>
                        {tVar(t, 'devices:drawer_section_connected_camera', {
                          number: cameraIndex + 1,
                        })}
                      </Text>
                      <DarkInfoField
                        colorScheme={colorScheme}
                        label={t('devices:drawer_field_camera_name')}
                        value={camera?.node?.name}
                      />
                      <DarkInfoField
                        colorScheme={colorScheme}
                        label={t('devices:drawer_field_camera_id')}
                        value={camera?.node?.id}
                      />
                    </Fragment>
                  )
                )}
              </VStack>
            </Card>
          ))
        )}
      </VStack>
    </VStack>
  )
}
